import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  File,
  FileImage,
  Film,
  MessageCircle,
  Paperclip,
  Send,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  SupportTicket,
  TicketAttachment,
  getSupportTicketById,
  makeAttachmentFromFile,
  replyToTicket,
  uploadTicketImage,
} from "./supportTicketsData";
import "./SupportPages.css";

type PendingAttachment = {
  file: File;
  preview: TicketAttachment;
};

const TicketMessage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ticketId = "" } = useParams();

  const decodedTicketId = decodeURIComponent(ticketId);
  const customerName =
    user?.fullName || user?.name || "Customer";
  const customerInitials = customerName
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const [ticket, setTicket] = useState<SupportTicket | null | undefined>(
    undefined,
  );
  const [replyText, setReplyText] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");

  const loadTicket = useCallback(async () => {
    try {
      setTicket(await getSupportTicketById(decodedTicketId));
    } catch {
      setTicket(null);
    }
  }, [decodedTicketId]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  const statusClass = useMemo(
    () =>
      ticket
        ? ticket.status.toLowerCase().replace(/\s+/g, "-")
        : "",
    [ticket],
  );

  const attachmentIcon = (attachment: TicketAttachment) => {
    if (attachment.kind === "image") {
      return <FileImage size={17} />;
    }
    if (attachment.kind === "video") {
      return <Film size={17} />;
    }
    return <File size={17} />;
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    setPendingAttachments((current) => [
      ...current,
      ...Array.from(files).map((file) => ({
        file,
        preview: makeAttachmentFromFile(file),
      })),
    ]);
  };

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();

    if (
      !ticket ||
      (!replyText.trim() && pendingAttachments.length === 0) ||
      sending
    ) {
      return;
    }

    setSending(true);
    setSendError("");

    try {
      // Upload image attachments (backend accepts images only)
      const uploaded = await Promise.all(
        pendingAttachments
          .filter((item) => item.preview.kind === "image")
          .map(async (item) => {
            const result = await uploadTicketImage(
              ticket.ticketId,
              item.file,
            );
            return {
              url: result.fileUrl,
              fileName: result.fileName || item.file.name,
              fileType: item.file.type,
              fileSize: item.file.size,
            };
          }),
      );

      await replyToTicket(
        ticket.ticketId,
        replyText.trim() || "Attachment sent.",
        uploaded.length ? uploaded : undefined,
      );

      setReplyText("");
      setPendingAttachments([]);
      await loadTicket();
    } catch (err: any) {
      setSendError(
        err?.response?.data?.message ||
          "Failed to send reply. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  if (ticket === null) {
    return (
      <section className="support-page">
        <div className="support-shell support-shell-narrow">
          <div className="support-empty-state">
            <MessageCircle size={30} />
            <h1>Ticket not found</h1>
            <p>
              This ticket is unavailable or no longer exists.
            </p>
            <button
              type="button"
              className="support-primary-button"
              onClick={() => navigate("/Tickets")}
            >
              Back to Chat List
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (ticket === undefined) {
    return (
      <section className="support-page">
        <div className="support-shell support-shell-narrow">
          <div className="support-empty-state">
            <MessageCircle size={30} />
            <h1>Loading ticket...</h1>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="support-page">
      <div className="support-shell support-shell-conversation">
        <header className="support-page-header support-conversation-header">
          <div className="support-heading">
            <button
              type="button"
              className="support-back-button"
              onClick={() => navigate("/Tickets")}
              aria-label="Back to Chat List"
            >
              <ArrowLeft size={16} />
            </button>

            <div>
              <p className="support-eyebrow">Ticket conversation</p>
              <h1>{ticket.subject}</h1>
              <p>Ticket {ticket.ticketId}</p>
            </div>
          </div>

          <div className="support-conversation-badges">
            <span
              className={`support-status support-status-${statusClass}`}
            >
              {ticket.status}
            </span>
            <span
              className={`support-severity support-severity-${ticket.severity.toLowerCase()}`}
            >
              {ticket.severity}
            </span>
          </div>
        </header>

        <div className="support-ticket-summary">
          <div>
            <span>Category</span>
            <strong>{ticket.category}</strong>
          </div>
          <div>
            <span>Opened by</span>
            <strong>{ticket.openedBy}</strong>
          </div>
          <div>
            <span>Language</span>
            <strong>{ticket.language}</strong>
          </div>
          <div>
            <span>Messages</span>
            <strong>{ticket.messages.length}</strong>
          </div>
        </div>

        <div className="support-conversation-card">
          <div className="support-conversation-title">
            <div>
              <h2>Messages</h2>
              <p>Complete conversation history.</p>
            </div>
          </div>

          <div className="support-message-list">
            {ticket.messages.map((message) => (
              <article
                key={message.id}
                className={`support-message support-message-${message.sender}`}
              >
                <div className="support-message-avatar">
                  {message.sender === "admin"
                    ? "S"
                    : customerInitials}
                </div>

                <div className="support-message-content">
                  <div className="support-message-meta">
                    <strong>{message.senderName}</strong>
                    <span>{message.sentAt}</span>
                  </div>

                  <p>{message.body}</p>

                  {!!message.attachments?.length && (
                    <div className="support-message-attachments">
                      {message.attachments.map((attachment) => (
                        <div
                          key={attachment.id}
                          className="support-message-attachment"
                        >
                          {attachment.kind === "image" &&
                          attachment.url ? (
                            <img
                              src={attachment.url}
                              alt={attachment.name}
                            />
                          ) : attachment.kind === "video" &&
                            attachment.url ? (
                            <video
                              src={attachment.url}
                              controls
                            />
                          ) : (
                            <span className="support-file-icon">
                              {attachmentIcon(attachment)}
                            </span>
                          )}
                          <div>
                            <strong>{attachment.name}</strong>
                            <span>{attachment.size}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>

          <form className="support-reply-box" onSubmit={sendReply}>
            <textarea
              rows={3}
              value={replyText}
              onChange={(event) =>
                setReplyText(event.target.value)
              }
              placeholder="Type your reply..."
            />

            {!!pendingAttachments.length && (
              <div className="support-attachment-chips">
                {pendingAttachments.map((item) => (
                  <div
                    key={item.preview.id}
                    className="support-attachment-chip"
                  >
                    <span>
                      {attachmentIcon(item.preview)}{" "}
                      {item.preview.name}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPendingAttachments((current) =>
                          current.filter(
                            (entry) =>
                              entry.preview.id !== item.preview.id,
                          ),
                        )
                      }
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {sendError && (
              <p className="support-form-error">{sendError}</p>
            )}

            <div className="support-reply-actions">
              <label className="support-attach-button">
                <Paperclip size={17} />
                Attach
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*,.pdf,.doc,.docx,.txt,.zip"
                  onChange={(event) =>
                    handleFiles(event.target.files)
                  }
                />
              </label>

              <button
                type="submit"
                className="support-primary-button"
                disabled={sending}
              >
                <Send size={17} />
                {sending ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default TicketMessage;
